import { memo, useCallback, useMemo, useState } from 'react';

import Icon from 'components/global/Icon';
import Popper from 'components/global/Popper';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import * as S from './DynamicListMenu.styled';

/**
 * @author Burak Unsal
 */

const ActionsMenu = (props) => {
	const {
		id,
		buttonIcon,
		buttonLabel,
		menuItems,
		emptyText = 'noMenuItems',
		selectionModel,
		showArrow = true,
		variant,
		disabled: disabledProp
	} = props;

	const [buttonRef, setButtonRef] = useState(null);

	const { showInfo } = useAlert();

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const selection = selectionModel.length === 1 ? selectionModel[0] : selectionModel;

	const items = useMemo(() => menuItems?.filter((item) => !item.footerItem && !item.headerItem), [menuItems]);
	const headerItems = useMemo(() => menuItems?.filter((item) => item.headerItem), [menuItems]);
	const footerItems = useMemo(() => menuItems?.filter((item) => item.footerItem), [menuItems]);
	const containsShowAlways = useMemo(() => menuItems?.some((item) => item.showAlways), [menuItems]);
	const disabled = !containsShowAlways && disabledProp;

	const handleClick = useCallback((e, isDisabled, action, closePopper) => {
		e.preventDefault();
		e.stopPropagation();
		if (isDisabled) return;
		if (!action) {
			showInfo({ header: 'alert.underDevelopment' });
			return;
		}
		action(selection);
		closePopper();
	});

	const renderMenuItems = useCallback(
		(item, index, closePopper) => {
			if (item.render && item.render(selection) === false) return;

			const isDisabled =
				disabledProp && !item.showAlways
					? 'errorMsg.selectAtLeastOneRow'
					: item.disabled
						? item.disabled(selection)
						: false;

			return (
				<Tooltip key={index} title={isDisabled} placement={isMobile ? 'top' : 'right'}>
					<S.MenuItem
						id={`${item.label}-action-menu-item`}
						className={`${item.className ?? ''} ${isDisabled ? 'disabled' : ''} ${
							item.label
						}-action-menu-item`}
						onContextMenu={(e) => handleClick(e, isDisabled, item.action, closePopper)}
						onClick={(e) => handleClick(e, isDisabled, item.action, closePopper)}>
						{item.icon && <Icon icon={item.icon} />}
						<Typography>{item.label}</Typography>
					</S.MenuItem>
				</Tooltip>
			);
		},
		[handleClick]
	);

	return (
		<>
			<Tooltip title={disabled ? 'errorMsg.selectAtLeastOneRow' : undefined} placement="bottom">
				<S.ActionMenuButton
					id={`${id}-action-menu-button`}
					ref={setButtonRef}
					variant={variant ?? 'transparent'}
					disabled={disabled}
					className={`${id}-action-menu-button`}>
					{buttonIcon && (
						<Icon
							fontSize="var(--paragraph3-font-size)"
							icon={buttonIcon}
							color={variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'}
						/>
					)}
					{buttonLabel && (
						<Typography color={variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'}>
							{buttonLabel}
						</Typography>
					)}
					{showArrow && (
						<Icon
							fontSize="var(--paragraph3-font-size)"
							icon={faAngleDown}
							color={variant ? 'var(--alwaysWhiteColor)' : 'var(--black)'}
						/>
					)}
				</S.ActionMenuButton>
			</Tooltip>

			{!disabled && buttonRef && (
				<Popper refElement={buttonRef}>
					{(closePopper) => (
						<S.Menu>
							{isMobile && (
								<Typography variant="p1" fontWeight="500" className="px-4">
									{buttonLabel}
								</Typography>
							)}
							{menuItems ? (
								<>
									{headerItems?.map((item, index) => renderMenuItems(item, index, closePopper))}
									{items?.map((item, index) => renderMenuItems(item, index, closePopper))}
									{footerItems?.length > 0 && <S.Line />}
									{footerItems?.map((item, index) => renderMenuItems(item, index, closePopper))}
								</>
							) : (
								<S.MenuItem className="disabled">
									<Typography>{emptyText}</Typography>
								</S.MenuItem>
							)}
						</S.Menu>
					)}
				</Popper>
			)}
		</>
	);
};

export default memo(ActionsMenu);
